import axios, { AxiosRequestConfig, Method } from 'axios';

import {
  resetAuthLocalStorage,
  verfiyUserActive,
  verifyAccessToken,
} from './auth';
import history from '../history';

/**
 * Every request gets a key (GET:/users).
 * Starting a new request with the same key aborts the previous one
 * only if REQUEST_CANCEL_THRESHOLD_MS (200ms) has elapsed since the
 * previous request was registered; bursts within that window run in parallel.
 * Aborted requests return a harmless empty response instead of throwing errors.
 * 401 responses trigger token refresh and retry.
 * 503 responses redirect to maintenance mode.
 * Other errors are logged and rejected normally.
 * Completed requests clean themselves up from the in-memory activeRequests map.
 * 
 * CANCELLATION SCOPE KEY (cancelKey):
 *   By default the cancellation key is `${method}:${normalizedUrl}`. When
 *   different logical operations share the same URL (e.g. two dashboard tabs
 *   both calling "dashboard/" with different host_type), provide a cancelKey
 *   as the 6th argument to give each operation its own cancellation scope.
 *   Convention: "endpoint/:logical-scope" (e.g. "dashboard/:ca-dashboard").
 * 
Component
    │
    ▼
callApi(url, method, params, data, headers, cancelKey?)
    │
    │   If cancelKey provided → use it as cancellation scope
    │   Else                  → use `${method}:${url}` (backwards compatible)
    │
    ▼
createRequestSignal(key)
    │
    ├── If previous request with same key is older than 200ms → cancel it
    └── Create new AbortController
    │
    ▼
axios.request()
    │
    ├── Success → Return response
    │
    ├── 401 → Refresh token → Retry
    │
    ├── 503 → Maintenance page
    │
    └── Cancelled → Return empty response
    │ 
    ▼
cleanup()
*/

/**
 * BUSINESS LOGIC: if on a single page/ui screen, same component is used multiple times
 * and may trigger multiple requests to the same endpoint,
 * then we want to allow both requests to run in parallel.
 * The 200ms threshold is to ensure those are triggered within a short time frame
 * (Example: policy list requests on PC dashboard)
 * Minimum gap (in ms) between two consecutive requests sharing the same key
 * before the earlier one is cancelled. Bursts within this window are allowed
 * to run in parallel; only a "real" later request triggers cancellation.
 */
const REQUEST_CANCEL_THRESHOLD_MS = 200;

interface ActiveRequest {
  controller: AbortController;
  timestamp: number;
}

/**
 * Stores active requests by key, along with the timestamp each one was started.
 * When a new request starts with the same key, the previous request is
 * cancelled only if at least REQUEST_CANCEL_THRESHOLD_MS has elapsed since
 * the previous one was registered.
 */
const activeRequests = new Map<string, ActiveRequest>();

// Creates a new request signal for the given key. If a previous request for
// the same key was registered at least REQUEST_CANCEL_THRESHOLD_MS ago, that
// request is aborted; otherwise it is left running and a new controller is
// returned. The cleanup function removes this request's entry from the map
// once the request is completed or failed, as long as no newer request has
// since replaced it.
function createRequestSignal(requestKey: string) {
  const now = Date.now();
  const existing = activeRequests.get(requestKey);

  // Cancel previous request for this key only if it is "stale" enough
  if (existing && now - existing.timestamp >= REQUEST_CANCEL_THRESHOLD_MS) {
    existing.controller.abort();
  }

  // Create new controller
  const controller = new AbortController();

  activeRequests.set(requestKey, { controller, timestamp: now });

  return {
    signal: controller.signal,
    cleanup: () => {
      const current = activeRequests.get(requestKey);
      if (current?.controller === controller) {
        activeRequests.delete(requestKey);
      }
    },
  };
}

// Bulk-cancel all in-flight requests tagged with the given companyId.
// Called on company switch to prevent stale old-company responses
// from overwriting new-company data in atoms.
export function cancelAllRequestsForCompany(companyId: number) {
  const prefix = `company-id:${companyId}:`;
  Array.from(activeRequests.entries()).forEach(([key, req]) => {
    if (key.startsWith(prefix)) {
      req.controller.abort();
      activeRequests.delete(key);
    }
  });
}

// Check if the error is due to request cancellation
// This can happen when a new request is made with the same key before the previous one completes
// In such cases, we want to ignore the error and return an empty response instead of rejecting the promise
// This is important to prevent unhandled promise rejections and to ensure that the UI remains responsive when users quickly change filters or navigate away
// The function checks if the error is an instance of AxiosError and if the error code is 'ERR_CANCELED' or the name is 'CanceledError', which indicates that the request was cancelled
function getCancelledResponse(error: any, config?: AxiosRequestConfig) {
  if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
    return {
      status: 499,
      statusText: 'Canceled',
      headers: {},
      config: config ?? ({} as AxiosRequestConfig),
      request: undefined,
      cancelled: true,
    } as any;
  } else {
    return null;
  }
}

// Note: please alwasys check the respose before using it, it can be an empty array in case of cancellation of request, which can break the expected structure of the API response and cause errors in the UI if not handled properly.
const callApi = async (
  url: string,
  method: Method,
  urlParams = {},
  data: any = null,
  headers: any = null,

  /**
   * Optional cancellation scope key. Overrides the default method:URL key used
   * for auto-cancelling duplicate in-flight requests.
   *
   * When to use:
   *   Multiple logical operations share the same URL but with different params,
   *   and those operations should NOT cancel each other's in-flight requests.
   *   Example: two dashboard tabs both calling 'dashboard/' with different
   *   `host_type` values — each tab should get its own cancellation scope so
   *   switching tabs doesn't abort the previous tab's request.
   *
   * Convention (example): 'dashboard/:ca-dashboard'
   *   - Left of `:`: the API endpoint path  (dashboard/)
   *   - Right of `:`: the logical scope     (ca-dashboard)
   *
   * When omitted, backwards-compatible default: `${method}:${normalizedUrl}`
   */
  cancelKey?: string,
) => {
  // Use a normalized URL for the key (so '/x', and 'x' share cancellation behavior)
  const normalizedKeyUrl = url.replace(/^\/+/, '');
  const baseKey = cancelKey ?? `${method}:${normalizedKeyUrl}`;

  // Prepend current company ID to the request key so all in-flight requests
  // for a company share a common prefix. This allows cancelAllRequestsForCompany
  // to bulk-abort requests from the old company on tenant switch.
  const storedId = parseInt(localStorage.getItem('currentCompanyId') || '');
  const companyId = storedId > 0 ? storedId : undefined;
  const requestKey = companyId ? `company-id:${companyId}:${baseKey}` : baseKey;

  // Create a new request signal for this key, which will automatically cancel any previous request with the same key
  const { signal, cleanup } = createRequestSignal(requestKey);

  const combineURL = (url: string) => {
    const server_path = process.env.REACT_APP_SERVER_PATH || '';
    if (url.startsWith(server_path)) {
      return new URL(url).href;
    } else {
      return new URL(`${process.env.REACT_APP_SERVER_PATH}/${url}`).href;
    }
  };

  try {
    let response;
    let error;

    if (url.startsWith('/api')) {
      url = url.replace(/^\/api/, '');
    }
    if (url.startsWith('/')) {
      url = url.substring(1);
    }
    try {
      response = await axios.request(getRequest());
    } catch (err) {
      error = err;
      // Check if the error is due to request cancellation
      const error_response = getCancelledResponse(error, getRequest());
      if (error_response) {
        return error_response;
      }
      // if its unauthorized response, then retry after verification of access token
      if (error.response?.status === 401) {
        verfiyUserActive(error.response);
        await verifyAccessToken();
        // retry and resend the request again
        try {
          error = null;
          response = await axios.request(getRequest());
        } catch (err) {
          error = err;
          // Check if the error is due to request cancellation
          const error_response = getCancelledResponse(error, getRequest());
          if (error_response) {
            return error_response;
          }
        }
      } else if (error.response?.status === 503) {
        handleMaintenanceMode();
        void Promise.reject(error);
      }
    }

    // asyncApiLogger(Date.now(), request, response, error);

    if (error) {
      logError(combineURL(url), method, urlParams, data, headers, error);
      return Promise.reject(error?.response ? error.response : error);
    }

    return Promise.resolve(response);
  } catch (error) {
    logError(url, method, urlParams, data, headers, error);
    return Promise.reject(error);
  } finally {
    // Clean up the active request for this key after the request is completed or failed
    cleanup();
  }

  function getRequest() {
    const authHeader = {
      Authorization: 'Bearer ' + localStorage.getItem('access'),
    };
    const request: AxiosRequestConfig = {
      url: combineURL(url),
      method,
      headers: {
        ...headers,
        ...authHeader,
      },
      params: urlParams,
      data,
      // Pass the signal to the axios request config to enable cancellation
      signal,
    };
    return request;
  }
};

export function handleMaintenanceMode() {
  // Handle the maintenance mode response here
  resetAuthLocalStorage();
  localStorage.setItem('maintenanceEnabled', 'true');
  if (window.location.pathname !== '/maintenance') {
    history.push('/maintenance');
  }
}

function logError(
  url: string,
  method: Method,
  urlParams,
  data: any,
  headers: any,
  error: any,
) {
  console.log({
    message: 'An error occurred calling back-end API',
    url,
    method,
    urlParams,
    data,
    headers,
    error: {
      response: error.response,
      name: error.name,
      message: error.message,
      stack: error.stack,
    },
  });
}

export function isCancellationResponse(response: any) {
  if (
    (response as any)?.status === 499 &&
    (response as any)?.statusText === 'Canceled'
  ) {
    return true;
  }
  return false;
}

export default callApi;
